import clsx from 'clsx';
import { useRef, useEffect, useState } from 'react';
import { ArrowDownIcon } from '@/components/icons/ArrowDownIcon';
import TrashIcon from '@/components/icons/TrashIcon';
import { Button } from '@/components/ui/Button';
import { FavoriteFilter } from '@/services/favoriteFiltersService';

type FilterSelectorDropdownProps = {
  filters: FavoriteFilter[];
  selectedFilterId: number | null;
  onSelect: (filter: FavoriteFilter) => void;
  onDelete: (filterId: number) => void;
};

export default function FilterSelectorDropdown({
  filters,
  selectedFilterId,
  onSelect,
  onDelete,
}: FilterSelectorDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selectedFilter = filters.find((f) => f.id === selectedFilterId);
  const displayText = selectedFilter?.name ?? 'Choose from saved filters';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isOpen) return;
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={ref} className="relative w-full sm:w-[220px]">
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full h-[40px] px-4 bg-[#F4F4F4] rounded-[10px] flex items-center justify-between text-sm font-medium font-avenir text-[#767676] hover:bg-[#EBEBEB] transition-colors cursor-pointer"
      >
        <span className={clsx('truncate', selectedFilter && 'text-black')}>{displayText}</span>
        <ArrowDownIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-[1000] mt-1">
          {filters.length === 0 ? (
            <div className="px-4 py-3 text-gray-400 text-sm font-avenir">No saved filters</div>
          ) : (
            <div className="max-h-[280px] overflow-y-auto notification-scrollbar">
              {filters.map((filter, index) => (
                <div
                  key={filter.id}
                  className={clsx(
                    'px-4 py-3 text-slate-600 text-sm font-medium cursor-pointer transition-all duration-200 ease-in-out hover:bg-slate-50 hover:text-blue-800 font-avenir flex items-center justify-between group',
                    index < filters.length - 1 && 'border-b border-gray-100',
                    selectedFilterId === filter.id && 'bg-slate-50 text-blue-800'
                  )}
                  onClick={() => {
                    onSelect(filter);
                    setIsOpen(false);
                  }}
                >
                  <span className="truncate mr-2">{filter.name}</span>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(filter.id);
                    }}
                    variant="icon"
                    className="shrink-0 text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <TrashIcon />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
